import {
  GenomicFeatureTypeEnum,
  GroupTypeEnum,
  SequenceAnalyzerFeatureType,
  SequencingTypeEnum,
  SyntenyTypeEnum,
  TableTypeEnum,
  TrackEnum,
} from "@selector/shared/openapi";
import { URL_ENTITY_TYPES } from "@selector/shared/settings";

import type { TrackType, UrlEntityType } from "../types.ts";

const EntityTypeToGenomicFeatureTypeMap: Partial<Record<UrlEntityType, GenomicFeatureTypeEnum>> = {
  [URL_ENTITY_TYPES.GENE]: GenomicFeatureTypeEnum.GENE,
  [URL_ENTITY_TYPES.PROTEIN]: GenomicFeatureTypeEnum.PROTEIN,
  [URL_ENTITY_TYPES.PROTEIN_LOCATION]: GenomicFeatureTypeEnum.PROTEIN_LOCATION,
  [URL_ENTITY_TYPES.TRANSCRIPT]: GenomicFeatureTypeEnum.TRANSCRIPT,
  [URL_ENTITY_TYPES.TRANSCRIPT_LOCATION]: GenomicFeatureTypeEnum.TRANSCRIPT_LOCATION,
  [URL_ENTITY_TYPES.NUFEATURE]: GenomicFeatureTypeEnum.NUCLEOTIDE_FEATURE,
};

const GenomicTypeToTrackTypeMap: Partial<Record<GenomicFeatureTypeEnum, TrackType>> = {
  GENE: TrackEnum.GENE,
  PROTEIN: TrackEnum.PROTEIN_LOCATION,
  PROTEIN_LOCATION: TrackEnum.PROTEIN_LOCATION,
  TRANSCRIPT: TrackEnum.TRANSCRIPT_LOCATION,
  TRANSCRIPT_LOCATION: TrackEnum.TRANSCRIPT_LOCATION,
  NUCLEOTIDE_FEATURE: TrackEnum.NUCLEOTIDE_FEATURE,
};

const TrackTypeToGenomicTypeMap: Partial<Record<TrackType, GenomicFeatureTypeEnum>> = {
  [TrackEnum.GENE]: GenomicFeatureTypeEnum.GENE,
  [TrackEnum.PROTEIN_LOCATION]: GenomicFeatureTypeEnum.PROTEIN_LOCATION,
  [TrackEnum.NUCLEOTIDE_FEATURE]: GenomicFeatureTypeEnum.NUCLEOTIDE_FEATURE,
  [TrackEnum.TRANSCRIPT_LOCATION]: GenomicFeatureTypeEnum.TRANSCRIPT_LOCATION,
};

const TrackTypeToEntityTypeMap: Partial<Record<TrackType, string>> = {
  [TrackEnum.GENE]: URL_ENTITY_TYPES.GENE,
  [TrackEnum.PROTEIN_LOCATION]: URL_ENTITY_TYPES.PROTEIN,
  [TrackEnum.TRANSCRIPT_LOCATION]: URL_ENTITY_TYPES.TRANSCRIPT,
  [TrackEnum.SEQUENCE]: URL_ENTITY_TYPES.SEQUENCE,
  [TrackEnum.NUCLEOTIDE_FEATURE]: URL_ENTITY_TYPES.NUFEATURE,
  [TrackEnum.AA_SEQUENCE_FEATURE]: URL_ENTITY_TYPES.AAFEATURE,
};

const TrackTypeToGroupTypeMap: Partial<Record<TrackType, GroupTypeEnum>> = {
  [TrackEnum.GENE]: GroupTypeEnum.GENE,
  [TrackEnum.TRANSCRIPT_LOCATION]: GroupTypeEnum.TRANSCRIPT,
  [TrackEnum.PROTEIN_LOCATION]: GroupTypeEnum.PROTEIN,
  [TrackEnum.NUCLEOTIDE_FEATURE]: GroupTypeEnum.NU_FEATURE,
  [TrackEnum.AA_SEQUENCE_FEATURE]: GroupTypeEnum.AA_FEATURE,
};

const TrackTypeToTableTypeMap: Partial<Record<TrackType, TableTypeEnum>> = {
  [TrackEnum.GENE]: TableTypeEnum.GENE,
  [TrackEnum.PROTEIN_LOCATION]: TableTypeEnum.PROTEIN_LOCATION,
  [TrackEnum.TRANSCRIPT_LOCATION]: TableTypeEnum.TRANSCRIPT_LOCATION,
  [TrackEnum.NUCLEOTIDE_FEATURE]: TableTypeEnum.NU_FEATURE,
  [TrackEnum.SEQUENCE]: TableTypeEnum.PANGENOME,
};

const TableTypeToTrackTypeMap: Partial<Record<TableTypeEnum, TrackType>> = {
  [TableTypeEnum.GENE]: TrackEnum.GENE,
  [TableTypeEnum.PROTEIN_LOCATION]: TrackEnum.PROTEIN_LOCATION,
  [TableTypeEnum.TRANSCRIPT_LOCATION]: TrackEnum.TRANSCRIPT_LOCATION,
  [TableTypeEnum.NU_FEATURE]: TrackEnum.NUCLEOTIDE_FEATURE,
  [TableTypeEnum.PANGENOME]: TrackEnum.SEQUENCE,
};

export function getGenomicFeatureTypeFromEntity(entityType: UrlEntityType): GenomicFeatureTypeEnum {
  return EntityTypeToGenomicFeatureTypeMap[entityType] ?? GenomicFeatureTypeEnum.PROTEIN;
}

export function getTrackTypeFromGenomicFeatureType(entityType: GenomicFeatureTypeEnum): TrackType {
  return GenomicTypeToTrackTypeMap[entityType] ?? TrackEnum.PROTEIN_LOCATION;
}

export function getGenomicFeatureTypeFromTrackType(trackType: TrackType): GenomicFeatureTypeEnum {
  return TrackTypeToGenomicTypeMap[trackType] ?? GenomicFeatureTypeEnum.PROTEIN_LOCATION;
}

export function getEntityTypeFromTrackType(trackType: TrackType): UrlEntityType {
  return (TrackTypeToEntityTypeMap[trackType] as UrlEntityType) ?? URL_ENTITY_TYPES.PROTEIN_LOCATION;
}

export function getGroupTypeFromTrackType(trackType: TrackType): GroupTypeEnum {
  return (TrackTypeToGroupTypeMap[trackType] as GroupTypeEnum) ?? GroupTypeEnum.PROTEIN;
}

export const getTrackTypeBySyntenyType = (syntenyType: SyntenyTypeEnum) => {
  return syntenyType === SyntenyTypeEnum.GENOME_ALIGNMENT ? TrackEnum.SEQUENCE : TrackEnum.PROTEIN_LOCATION;
};

export const getSyntenyTypeBySequencingType = (sequencingType: SequencingTypeEnum) => {
  return sequencingType === SequencingTypeEnum.PANGENOME ? SyntenyTypeEnum.GENOME_ALIGNMENT : SyntenyTypeEnum.PROTEIN;
};

export const getTrackTypeBySequencingType = (sequencingType: SequencingTypeEnum) => {
  return sequencingType === SequencingTypeEnum.PANGENOME ? TrackEnum.SEQUENCE : TrackEnum.PROTEIN_LOCATION;
};

export function getSequenceAnalyzerFeatureTypeFromTrackType(
  trackType: TrackEnum,
): SequenceAnalyzerFeatureType | undefined {
  if (trackType === TrackEnum.GENE) {
    return SequenceAnalyzerFeatureType.gene;
  } else if (trackType === TrackEnum.PROTEIN_LOCATION) {
    return SequenceAnalyzerFeatureType.proteinLocation;
  } else if (trackType === TrackEnum.TRANSCRIPT_LOCATION) {
    return SequenceAnalyzerFeatureType.transcriptLocation;
  }

  return undefined;
}

export function getTableTypeFromTrackType(trackType: TrackType): TableTypeEnum {
  return (TrackTypeToTableTypeMap[trackType] as TableTypeEnum) ?? undefined;
}

export function getTrackTypeFromTableType(tableType: TableTypeEnum): TrackType {
  return (TableTypeToTrackTypeMap[tableType] as TrackEnum) ?? undefined;
}
